import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import styles from "../styles/FixturePage.module.css";

const FEATURED_STATS = ["Ball Possession", "Shots on Goal", "Corner Kicks"];

const valueText = (stat) => {
  if (stat?.value === null || stat?.value === undefined || stat?.value === "") return "-";
  return String(stat.value);
};

const numericValue = (stat) => {
  const value = Number(stat?.number);
  return Number.isFinite(value) ? value : 0;
};

const MatchStatsSection = ({ fixtureId, homeTeam, awayTeam }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!fixtureId) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.get("/football/match-stats", {
          params: { fixture_id: fixtureId, period: "all" },
        });
        if (!cancelled) setPayload(response.data || null);
      } catch (err) {
        console.error("Error fetching match stats:", err);
        if (!cancelled) setError("Match statistics are not available yet.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fixtureId]);

  const stats = useMemo(() => {
    const teams = Array.isArray(payload?.teams) ? payload.teams : [];
    const home = teams[0] || {};
    const away = teams[1] || {};
    const homeMap = new Map((home.statistics || []).map((stat) => [stat.type, stat]));
    const awayMap = new Map((away.statistics || []).map((stat) => [stat.type, stat]));
    const labels = [...new Set([...homeMap.keys(), ...awayMap.keys()])];

    return {
      home,
      away,
      rows: labels.map((label) => ({
        label,
        home: homeMap.get(label),
        away: awayMap.get(label),
      })),
    };
  }, [payload]);

  if (loading) return <div className={styles.matchEmpty}>Loading match statistics...</div>;
  if (error || !stats.rows.length) {
    return <div className={styles.matchEmpty}>{error || payload?.reason || "No match statistics available yet."}</div>;
  }

  const featuredRows = FEATURED_STATS.map((label) =>
    stats.rows.find((row) => row.label === label)
  ).filter(Boolean);

  return (
    <section className={styles.statsPanel}>
      <div className={styles.sectionTitleRow}>
        <div>
          <span className={styles.sectionEyebrow}>Match centre</span>
          <h2>Key stats</h2>
        </div>
        <span className={styles.liveDataBadge}>Official match data</span>
      </div>

      {!!featuredRows.length && (
        <div className={styles.featuredStatsGrid}>
          {featuredRows.map((row) => {
            const homeValue = numericValue(row.home);
            const awayValue = numericValue(row.away);
            const total = homeValue + awayValue;
            const homeWidth = total > 0 ? Math.max(8, (homeValue / total) * 100) : 50;
            return (
              <article key={row.label} className={styles.featuredStatCard}>
                <span>{row.label}</span>
                <div className={styles.statTeamLead}>
                  {homeValue >= awayValue ? homeTeam : awayTeam}
                </div>
                <div className={styles.statValues}>
                  <strong>{valueText(row.home)}</strong>
                  <strong>{valueText(row.away)}</strong>
                </div>
                <div className={styles.statTrack}>
                  <span style={{ width: `${homeWidth}%` }} />
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className={styles.fullStatsTable}>
        <div className={styles.statsTeamHeader}>
          <strong>{stats.home?.team?.name || homeTeam}</strong>
          <span>Full match</span>
          <strong>{stats.away?.team?.name || awayTeam}</strong>
        </div>
        {stats.rows.map((row) => (
          <div key={row.label} className={styles.statsRow}>
            <strong>{valueText(row.home)}</strong>
            <span>{row.label}</span>
            <strong>{valueText(row.away)}</strong>
          </div>
        ))}
      </div>
    </section>
  );
};

export default MatchStatsSection;
